import { createFileRoute } from '@tanstack/react-router';
import { Card, SectionLabel, Wordmark } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import { entraErrorDetail } from '@/lib/entraErrorDetail.ts';

/*
 * Where Entra sends the customer's IT after the admin consent screen of their
 * own agendaproxy app.
 *
 * It sits outside `_app` on purpose: the administrator who consents is often
 * not a Wundo user at all, and has no session here.
 */
export const Route = createFileRoute('/agendaproxy/consent')({
  component: AgendaProxyConsentPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: search.status === 'ok' ? ('ok' as const) : ('error' as const),
    code: typeof search.code === 'string' ? search.code : undefined,
    description: typeof search.description === 'string' ? search.description : undefined,
  }),
});

function AgendaProxyConsentPage() {
  const { status, code, description } = Route.useSearch();
  const detail = entraErrorDetail(code, description);

  return (
    <main className='mx-auto flex min-h-dvh max-w-150 flex-col gap-6 px-6 py-12'>
      <div>
        <Wordmark registered className='h-8' />
      </div>

      {status === 'ok' ? (
        <Card tone='accent'>
          <h1 className='text-xl font-semibold tracking-tight text-success'>
            {m.agendaproxy_consent_ok_title()}
          </h1>
          <p className='mt-3 text-base text-ink-muted'>
            {m.agendaproxy_consent_ok_body()}
          </p>
        </Card>
      ) : (
        <Card className='border-danger-line bg-danger-fill'>
          <h1 className='text-xl font-semibold tracking-tight text-danger'>
            {m.agendaproxy_consent_error_title()}
          </h1>
          <p className='mt-3 text-base'>{m.agendaproxy_consent_error_body()}</p>

          {/* Entra's own words: an administrator pastes them into a ticket or a search box. */}
          {detail && (
            <div className='mt-4'>
              <SectionLabel as='h2'>{m.agendaproxy_consent_error_detail()}</SectionLabel>
              <pre className='mt-1.5 overflow-x-auto rounded-md bg-surface px-3.5 py-2.5 font-mono text-sm whitespace-pre-wrap'>
                {detail}
              </pre>
            </div>
          )}
        </Card>
      )}
    </main>
  );
}
